import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Icon } from "@iconify/react/dist/iconify.js";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import { useTranslation } from "../i18n/I18nContext";
import { listUsers, activateUser, inactivateUser, resetUserPassword } from "../services/user";
import { getUserInfo } from "../services/auth";
import { useConfirm } from "../hook/useConfirm";

const PAGE_SIZE = 8;

const isUserActive = (user) => String(user.active) === "1";

const formatDate = (value) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleDateString("pt-PT", { day: "2-digit", month: "short", year: "numeric" });
};

// Ported from sge_infantil_web/src/pages/user/maintain/index.js. The list
// endpoint is `GET /api/user/all/{sucursalId}` (not `/api/user/{sucursalId}`
// as first assumed) — each record nests the profile as `profile.description`.
// Layout follows the same expand-row / portal-actions-menu pattern as
// ListaEstudantesLayer; email and register date moved out of the main row
// into the expanded details, and a "reset password" action was ported from
// maintain/index.js `handleResetPassword` (PUT /api/user/reset-password/:id).
const ListaUtilizadoresLayer = () => {
  const { t } = useTranslation();
  const { confirm, ConfirmModal } = useConfirm();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [expandedId, setExpandedId] = useState(null);
  const [actionLoadingId, setActionLoadingId] = useState(null);
  // Same portal-menu pattern as ListaEstudantesLayer: rendered into
  // document.body and positioned with `position: fixed` so it isn't clipped
  // by the `.table-responsive` scroll container.
  const [openMenu, setOpenMenu] = useState(null); // { id, top, left, record }
  const menuRef = useRef(null);

  const fetchUsers = () => {
    setLoading(true);
    listUsers()
      .then((resp) => setUsers(resp.data || []))
      .catch(() => setUsers([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return users;
    return users.filter(
      (u) =>
        u.name?.toLowerCase().includes(term) ||
        u.email?.toLowerCase().includes(term) ||
        u.contact?.toLowerCase().includes(term) ||
        u.profile?.description?.toLowerCase().includes(term),
    );
  }, [users, search]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageItems = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const goToPage = (next) => {
    if (next < 1 || next > totalPages) return;
    setPage(next);
  };

  const handleSearch = (e) => {
    setSearch(e.target.value);
    setPage(1);
  };

  const toggleExpand = (id) => setExpandedId((cur) => (cur === id ? null : id));

  const MENU_WIDTH = 240;

  const toggleMenu = (e, record) => {
    e.stopPropagation();
    if (openMenu?.id === record.id) {
      setOpenMenu(null);
      return;
    }
    // Read currentTarget synchronously — React nulls it out once the event
    // finishes dispatching, and in StrictMode the setOpenMenu updater above
    // used to be re-invoked later (to detect impure reducers), by which
    // point `e.currentTarget` was already null.
    const rect = e.currentTarget.getBoundingClientRect();
    const left = Math.max(8, Math.min(rect.right - MENU_WIDTH, window.innerWidth - MENU_WIDTH - 8));
    setOpenMenu({ id: record.id, top: rect.bottom + 6, left, record });
  };

  const closeMenu = () => setOpenMenu(null);

  useEffect(() => {
    if (!openMenu) return undefined;
    const handlePointerDown = (e) => {
      if (e.target.closest("[data-user-actions]")) return;
      closeMenu();
    };
    const handleEscape = (e) => {
      if (e.key === "Escape") closeMenu();
    };
    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleEscape);
    window.addEventListener("scroll", closeMenu, true);
    window.addEventListener("resize", closeMenu);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleEscape);
      window.removeEventListener("scroll", closeMenu, true);
      window.removeEventListener("resize", closeMenu);
    };
  }, [openMenu]);

  const handleEdit = (user) => {
    navigate(`/utilizadores/editar/${user.id}`, { state: { user } });
  };

  const handleToggleActive = async (user) => {
    setActionLoadingId(user.id);
    const active = isUserActive(user);
    try {
      if (active) {
        await inactivateUser(user.id, getUserInfo()?.id);
        toast.success(t("utilizador.list.inactivate.success"));
      } else {
        await activateUser(user.id, getUserInfo()?.id);
        toast.success(t("utilizador.list.activate.success"));
      }
      fetchUsers();
    } catch (err) {
      toast.error(t("utilizador.list.toggle.error"));
    } finally {
      setActionLoadingId(null);
    }
  };

  const handleResetPassword = async (user) => {
    if (!(await confirm(t("utilizador.list.resetPassword.confirm"), { variant: "warning" }))) return;
    setActionLoadingId(user.id);
    try {
      await resetUserPassword(user.id, getUserInfo()?.id);
      toast.success(t("utilizador.list.resetPassword.success"));
    } catch (err) {
      toast.error(t("utilizador.list.resetPassword.error"));
    } finally {
      setActionLoadingId(null);
    }
  };

  return (
    <div className='row gy-4'>
      <ToastContainer />
      {ConfirmModal}

      <div className='col-12'>
        <div className='card radius-12'>
          <div className='card-body'>
            <div className='d-flex flex-wrap align-items-center justify-content-between gap-3 mb-24'>
              <div>
                <h6 className='mb-4'>{t("utilizador.list.title")}</h6>
                <p className='text-secondary-light mb-0 text-sm'>{t("utilizador.list.subtitle")}</p>
              </div>
              <button
                type='button'
                className='btn btn-primary-600 d-flex align-items-center gap-2'
                onClick={() => navigate("/utilizadores/cadastrar")}
              >
                <Icon icon='ic:baseline-plus' />
                {t("utilizador.list.add")}
              </button>
            </div>

            <div className='mb-16'>
              <div className='icon-field max-w-320-px'>
                <span className='icon top-50 translate-middle-y'>
                  <Icon icon='ion:search-outline' />
                </span>
                <input
                  type='text'
                  className='form-control'
                  placeholder={t("utilizador.list.search.placeholder")}
                  value={search}
                  onChange={handleSearch}
                />
              </div>
            </div>

            <div className='table-responsive scroll-sm'>
              <table className='table bordered-table mb-0'>
                <thead>
                  <tr>
                    <th scope='col' style={{ width: 36 }} />
                    <th scope='col'>{t("global.name")}</th>
                    <th scope='col'>{t("global.contact")}</th>
                    <th scope='col'>{t("utilizador.field.profile")}</th>
                    <th scope='col'>{t("global.status")}</th>
                    <th scope='col' className='text-end'>
                      {t("global.col.actions")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {loading && (
                    <tr>
                      <td colSpan={6} className='text-center text-secondary-light'>
                        {t("utilizador.list.loading")}
                      </td>
                    </tr>
                  )}
                  {!loading && pageItems.length === 0 && (
                    <tr>
                      <td colSpan={6} className='text-center text-secondary-light'>
                        {t("utilizador.list.empty")}
                      </td>
                    </tr>
                  )}
                  {pageItems.map((user) => {
                    const isExpanded = expandedId === user.id;
                    const busy = actionLoadingId === user.id;
                    return (
                      <React.Fragment key={user.id}>
                        <tr
                          role='button'
                          onClick={() => toggleExpand(user.id)}
                          className={isExpanded ? "bg-neutral-50" : ""}
                        >
                          <td>
                            <Icon
                              icon={isExpanded ? "ic:round-expand-less" : "ic:round-expand-more"}
                              className='text-secondary-light text-xl'
                            />
                          </td>
                          <td className='fw-semibold text-primary-light'>{user.name}</td>
                          <td>{user.contact || "-"}</td>
                          <td>{user.profile?.description || "-"}</td>
                          <td>
                            <span
                              className={`px-10 py-4 radius-8 fw-medium text-sm ${
                                isUserActive(user)
                                  ? "bg-success-focus text-success-main"
                                  : "bg-danger-focus text-danger-main"
                              }`}
                            >
                              {isUserActive(user) ? t("global.active") : t("global.inactive")}
                            </span>
                          </td>
                          <td className='text-end' onClick={(e) => e.stopPropagation()}>
                            <button
                              type='button'
                              data-user-actions
                              className='w-32-px h-32-px d-inline-flex align-items-center justify-content-center radius-8 border bg-base'
                              aria-expanded={openMenu?.id === user.id}
                              disabled={busy}
                              onClick={(e) => toggleMenu(e, user)}
                            >
                              <Icon icon='entypo:dots-three-vertical' />
                            </button>
                          </td>
                        </tr>
                        {isExpanded && (
                          <tr>
                            <td colSpan={6} className='p-0'>
                              <div className='p-24 bg-neutral-25 border-top'>
                                <div className='row gy-3'>
                                  <div className='col-md-6'>
                                    <table className='table table-bordered table-sm mb-0 bg-base'>
                                      <tbody>
                                        <tr>
                                          <th className='w-40 bg-neutral-50'>{t("global.email")}</th>
                                          <td>{user.email || "-"}</td>
                                        </tr>
                                        <tr>
                                          <th className='bg-neutral-50'>
                                            {t("utilizador.expand.username")}
                                          </th>
                                          <td>{user.username || "-"}</td>
                                        </tr>
                                        <tr>
                                          <th className='bg-neutral-50'>
                                            {t("utilizador.expand.registerDate")}
                                          </th>
                                          <td>{formatDate(user.createdAt)}</td>
                                        </tr>
                                      </tbody>
                                    </table>
                                  </div>
                                  <div className='col-md-6'>
                                    <span className='text-sm fw-medium text-secondary-light d-block mb-8'>
                                      {t("utilizador.field.sucursais")}
                                    </span>
                                    <div className='d-flex flex-wrap gap-8'>
                                      {(user.sucursals || []).length === 0 && (
                                        <span className='text-secondary-light text-sm'>-</span>
                                      )}
                                      {(user.sucursals || []).map((s) => (
                                        <span
                                          key={s.id}
                                          className='px-12 py-4 radius-8 fw-medium text-sm bg-primary-50 text-primary-600'
                                        >
                                          {s.description || s.name}
                                        </span>
                                      ))}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className='d-flex flex-wrap align-items-center justify-content-between gap-3 mt-16'>
              <span className='text-secondary-light text-sm'>
                {filtered.length} {t("utilizador.list.total")}
              </span>
              {!loading && filtered.length > PAGE_SIZE && (
                <ul className='pagination d-flex flex-wrap align-items-center gap-2 mb-0'>
                  <li className='page-item'>
                    <button
                      type='button'
                      className='page-link text-secondary-light fw-medium radius-4 border-0 px-10 py-10 d-flex align-items-center justify-content-center h-32-px w-32-px bg-base'
                      onClick={() => goToPage(page - 1)}
                      disabled={page === 1}
                    >
                      <Icon icon='ep:d-arrow-left' className='text-xl' />
                    </button>
                  </li>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                    <li key={p} className='page-item'>
                      <button
                        type='button'
                        className={`page-link fw-medium radius-4 border-0 px-10 py-10 d-flex align-items-center justify-content-center h-32-px w-32-px ${
                          p === page ? "bg-primary-600 text-white" : "bg-primary-50 text-secondary-light"
                        }`}
                        onClick={() => goToPage(p)}
                      >
                        {p}
                      </button>
                    </li>
                  ))}
                  <li className='page-item'>
                    <button
                      type='button'
                      className='page-link text-secondary-light fw-medium radius-4 border-0 px-10 py-10 d-flex align-items-center justify-content-center h-32-px w-32-px bg-base'
                      onClick={() => goToPage(page + 1)}
                      disabled={page === totalPages}
                    >
                      <Icon icon='ep:d-arrow-right' className='text-xl' />
                    </button>
                  </li>
                </ul>
              )}
            </div>
          </div>
        </div>
      </div>

      {openMenu &&
        createPortal(
          <ul
            ref={menuRef}
            data-user-actions
            className='dropdown-menu show p-12 border bg-base shadow'
            style={{ position: "fixed", top: openMenu.top, left: openMenu.left, width: MENU_WIDTH, zIndex: 1080 }}
          >
            <li>
              <button
                type='button'
                className='dropdown-item px-16 py-8 rounded text-secondary-light bg-hover-neutral-200 text-hover-neutral-900 d-flex align-items-center gap-10'
                onClick={() => {
                  closeMenu();
                  handleEdit(openMenu.record);
                }}
              >
                <Icon icon='solar:pen-2-linear' /> {t("global.edit")}
              </button>
            </li>
            <li>
              <button
                type='button'
                className={`dropdown-item px-16 py-8 rounded d-flex align-items-center gap-10 ${
                  isUserActive(openMenu.record)
                    ? "text-secondary-light bg-hover-danger-100 text-hover-danger-600"
                    : "text-secondary-light bg-hover-success-100 text-hover-success-600"
                }`}
                onClick={() => {
                  closeMenu();
                  handleToggleActive(openMenu.record);
                }}
              >
                <Icon icon={isUserActive(openMenu.record) ? "ic:outline-block" : "ic:outline-check-circle"} />
                {isUserActive(openMenu.record)
                  ? t("utilizador.list.inactivate")
                  : t("utilizador.list.activate")}
              </button>
            </li>
            <li>
              <hr className='dropdown-divider' />
            </li>
            <li>
              <button
                type='button'
                className='dropdown-item px-16 py-8 rounded text-secondary-light bg-hover-warning-100 text-hover-warning-600 d-flex align-items-center gap-10'
                onClick={() => {
                  closeMenu();
                  handleResetPassword(openMenu.record);
                }}
              >
                <Icon icon='solar:key-linear' /> {t("utilizador.list.action.resetPassword")}
              </button>
            </li>
          </ul>,
          document.body,
        )}
    </div>
  );
};

export default ListaUtilizadoresLayer;
